import React from 'react';
import { Link } from 'react-router-dom';

function MotivationGuide({ darkMode }) {
  const cardClass = darkMode 
    ? 'bg-gray-800 text-white' 
    : 'bg-white text-gray-800';

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        
        {/* 헤더 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            지원동기 & 입사포부 완벽 답변법
          </h1>
          <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            면접관이 가장 중요하게 생각하는 질문! 회사에 대한 진정성과 열정을 보여주세요.
          </p>
        </div>

        {/* 이 질문이 중요한 이유 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">❓</span>
            면접관이 이 질문을 하는 이유
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">🎯 진정성 확인</h3>
              <p className="text-sm leading-relaxed">
                정말로 이 회사에서 일하고 싶어 하는지, 아니면 그냥 아무 회사나 지원한 건지를 판단하려고 합니다.
                회사에 대해 얼마나 관심 있게 조사했는지도 확인해요.
              </p>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">💼 업무 적합성</h3>
              <p className="text-sm leading-relaxed">
                지원자가 이 직무와 회사 문화에 잘 맞는지 확인합니다.
                장기적으로 회사에 기여할 수 있는 인재인지 판단하려고 해요.
              </p>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">🔥 열정도 측정</h3>
              <p className="text-sm leading-relaxed">
                단순히 스펙만 좋은 게 아니라, 정말 열정적으로 일할 수 있는 사람인지 확인합니다.
                회사와 직무에 대한 이해도도 함께 평가해요.
              </p>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
              <h3 className="font-bold mb-3 text-red-600">📈 성장 가능성</h3>
              <p className="text-sm leading-relaxed">
                미래에 대한 계획이 명확하고 회사와 함께 성장할 수 있는 인재인지 봅니다.
                단기가 아닌 장기적 관점에서 평가하려고 해요.
              </p>
            </div>
          </div>
        </div>

        {/* 지원동기 답변 구조 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">💼</span>
            지원동기 완벽 답변 구조 (4단계)
          </h2>
          
          <div className="space-y-6">
            {/* 1단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                <h3 className="font-bold text-blue-600">나의 핵심 가치관 제시</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 [핵심 가치]입니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-blue-600">예시:</p>
                <p className="text-sm italic">
                  "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 고객 가치 창출을 통한 사회적 임팩트입니다."
                </p>
              </div>
            </div>

            {/* 2단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                <h3 className="font-bold text-green-600">최근 이슈/트렌드 연결</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "최근 [관련 이슈/트렌드]를 보며 더욱 확신하게 되었습니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-green-600">예시:</p>
                <p className="text-sm italic">
                  "최근 개인정보보호 강화와 쿠키리스 시대로 전환되면서, 프라이버시를 존중하면서도 개인화 서비스를 제공하는 것이 핵심 과제가 되었다고 생각합니다."
                </p>
              </div>
            </div>

            {/* 3단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-purple-500 ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                <h3 className="font-bold text-purple-600">회사 구체적 사례와 부합성</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "○○기업이 [구체적 사례]를 통해 이런 가치를 실현하고 있다는 점에서 깊이 공감했습니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-purple-600">예시:</p>
                <p className="text-sm italic">
                  "○○기업이 작년 개인정보보호 우수기업 인증을 받으면서도 고객 만족도 1위를 달성한 것을 보며, 기술과 윤리가 조화된 마케팅을 실현하고 있다는 점에서 깊이 공감했습니다."
                </p>
              </div>
            </div>

            {/* 4단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-red-500 ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</div>
                <h3 className="font-bold text-red-600">내가 기여할 수 있는 것</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "제가 보유한 [역량]으로 ○○기업의 [목표]에 기여하고 싶습니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-red-600">예시:</p>
                <p className="text-sm italic">
                  "제가 보유한 데이터 분석 능력과 고객 인사이트 도출 경험으로 ○○기업의 지속가능한 성장에 기여하고 싶습니다."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 입사후포부 답변 구조 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🚀</span>
            입사후포부 완벽 답변 구조 (3단계)
          </h2>
          
          <div className="space-y-6">
            {/* 1단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-orange-500 ${darkMode ? 'bg-orange-900 bg-opacity-20' : 'bg-orange-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                <h3 className="font-bold text-orange-600">단기적 목표 (1-2년)</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "단기적으로는 [구체적 목표]를 달성하겠습니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-orange-600">예시:</p>
                <p className="text-sm italic">
                  "단기적으로는 퍼스트파티 데이터 활용 전문성을 키워 고객 이탈률 10% 감소라는 구체적 성과를 달성하겠습니다."
                </p>
              </div>
            </div>

            {/* 2단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-teal-500 ${darkMode ? 'bg-teal-900 bg-opacity-20' : 'bg-teal-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                <h3 className="font-bold text-teal-600">장기적 비전 (3-5년)</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "장기적으로는 [더 큰 목표/전문가 비전]을 이루고 싶습니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-teal-600">예시:</p>
                <p className="text-sm italic">
                  "장기적으로는 프라이버시 친화적 마케팅 전략을 리드하는 마케팅 전문가로 성장하고 싶습니다."
                </p>
              </div>
            </div>

            {/* 3단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-indigo-500 ${darkMode ? 'bg-indigo-900 bg-opacity-20' : 'bg-indigo-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                <h3 className="font-bold text-indigo-600">구체적 목표 달성 방법</h3>
              </div>
              <p className="text-sm leading-relaxed mb-3">
                "이를 위해 [구체적 실행 계획]을 수립했습니다."
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-indigo-600">예시:</p>
                <p className="text-sm italic">
                  "이를 위해 첫째, 입사 즉시 쿠키리스 환경에서의 고객 분석 역량을 강화하겠습니다. 둘째, 사내 데이터 거버넌스 교육에 적극 참여하고, 셋째, 개인정보보호와 개인화의 균형점을 찾는 프로젝트를 주도적으로 기획해 실무 경험을 쌓겠습니다."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 완성 예시 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎭</span>
            완성된 답변 예시 (마케팅 직무)
          </h2>
          
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gradient-to-r from-green-900 to-blue-900 bg-opacity-30' : 'bg-gradient-to-r from-green-50 to-blue-50'} border-l-4 border-yellow-500`}>
            <h3 className="font-bold mb-4 text-lg">💼 지원동기</h3>
            <p className="text-sm leading-relaxed mb-6">
              <span className="font-semibold text-blue-600">[가치관]</span> 
              "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 고객 가치 창출을 통한 사회적 임팩트입니다."
              <br/><br/>
              <span className="font-semibold text-green-600">[최근 이슈]</span> 
              "최근 개인정보보호 강화와 쿠키리스 시대로 전환되면서, 고객 프라이버시를 존중하면서도 개인화 서비스를 제공하는 것이 마케팅의 핵심 과제가 되었다고 생각합니다."
              <br/><br/>
              <span className="font-semibold text-purple-600">[회사 부합성]</span> 
              "○○기업이 작년 개인정보보호 우수기업 인증을 받으면서도 고객 만족도 1위를 달성한 것을 보며, 기술과 윤리가 조화된 마케팅을 실현하고 있다는 점에서 깊이 공감했습니다."
              <br/><br/>
              <span className="font-semibold text-red-600">[기여 방안]</span> 
              "제가 보유한 데이터 분석 능력과 고객 인사이트 도출 경험으로 ○○기업의 지속가능한 성장에 기여하고 싶습니다."
            </p>

            <h3 className="font-bold mb-4 text-lg">🚀 입사후포부</h3>
            <p className="text-sm leading-relaxed">
              <span className="font-semibold text-orange-600">[단기 목표]</span> 
              "단기적으로는 퍼스트파티 데이터 활용 전문성을 키워 고객 이탈률 10% 감소라는 구체적 성과를 달성하겠습니다."
              <br/><br/>
              <span className="font-semibold text-teal-600">[장기 비전]</span> 
              "장기적으로는 프라이버시 친화적 마케팅 전략을 리드하는 마케팅 전문가로 성장하고 싶습니다."
              <br/><br/>
              <span className="font-semibold text-indigo-600">[달성 방법]</span> 
              "이를 위해 첫째, 입사 즉시 쿠키리스 환경에서의 고객 분석 역량을 강화하겠습니다. 둘째, 사내 데이터 거버넌스 교육에 적극 참여하고, 셋째, 개인정보보호와 개인화의 균형점을 찾는 프로젝트를 주도적으로 기획해 실무 경험을 쌓겠습니다."
            </p>
          </div>
        </div>

        {/* 직무별 예시 답변 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">💼</span>
            직무별 답변 예시
          </h2>
          
          <div className="space-y-6">
            {/* IT개발자 예시 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <h3 className="font-bold mb-3 text-blue-600">💻 IT 개발자</h3>
              <p className="text-sm leading-relaxed">
                <strong>[지원동기]</strong> "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 기술을 통한 사회 문제 해결입니다. 
                최근 AI 윤리와 알고리즘 공정성 이슈가 대두되면서, ○○기업이 'AI 윤리 위원회' 설립과 공정한 AI 개발 가이드라인을 제시한 것을 보며 깊이 공감했습니다."
                <br/><br/>
                <strong>[입사후포부]</strong> "단기적으로는 클린 코드 작성과 테스트 자동화 역량을 강화해 코드 품질 개선에 기여하겠습니다. 
                장기적으로는 확장 가능한 시스템 아키텍처를 설계하는 시니어 개발자로 성장하고 싶습니다. 
                이를 위해 사내 코드 리뷰 문화에 적극 참여하고, 오픈소스 프로젝트 기여를 통해 지속적으로 학습하겠습니다."
              </p>
            </div>

            {/* 영업 예시 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'} border-l-4 border-green-500`}>
              <h3 className="font-bold mb-3 text-green-600">💼 영업</h3>
              <p className="text-sm leading-relaxed">
                <strong>[지원동기]</strong> "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 고객과의 진정한 파트너십 구축입니다. 
                최근 B2B 시장에서 단순 제품 판매보다는 솔루션 제공이 중요해지면서, ○○기업이 고객 성공팀을 신설하고 장기적 관계에 집중하는 모습을 보며 지원했습니다."
                <br/><br/>
                <strong>[입사후포부]</strong> "단기적으로는 고객 니즈 분석 역량을 키워 신규 고객 10팀 확보라는 목표를 달성하겠습니다. 
                장기적으로는 주요 계정 관리 전문가로서 고객 만족도 95% 이상을 유지하는 영업 리더로 성장하고 싶습니다. 
                이를 위해 CRM 시스템 활용 능력을 강화하고, 업계 트렌드 분석을 통한 컨설팅형 영업 역량을 기르겠습니다."
              </p>
            </div>

            {/* 디자이너 예시 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'} border-l-4 border-purple-500`}>
              <h3 className="font-bold mb-3 text-purple-600">🎨 디자이너</h3>
              <p className="text-sm leading-relaxed">
                <strong>[지원동기]</strong> "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 사용자 중심의 디자인 철학입니다. 
                최근 디지털 접근성과 포용적 디자인이 강조되면서, ○○기업이 시각 장애인을 위한 웹 접근성 개선 프로젝트를 선도적으로 진행한 것을 보며 깊이 공감했습니다."
                <br/><br/>
                <strong>[입사후포부]</strong> "단기적으로는 사용자 리서치와 데이터 분석 능력을 강화해 사용자 만족도 20% 향상에 기여하겠습니다. 
                장기적으로는 디자인 시스템을 구축하고 UX 전략을 주도하는 디자인 리더로 성장하고 싶습니다. 
                이를 위해 사용자 인터뷰 스킬을 기르고, 프로토타이핑 툴 전문성을 높이며, 개발팀과의 협업 능력을 지속적으로 발전시키겠습니다."
              </p>
            </div>

            {/* 인사 예시 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'} border-l-4 border-red-500`}>
              <h3 className="font-bold mb-3 text-red-600">👥 인사</h3>
              <p className="text-sm leading-relaxed">
                <strong>[지원동기]</strong> "제가 기업을 선택할 때 가장 중요하게 생각하는 것은 직원의 성장과 행복을 진심으로 지원하는 조직문화입니다. 
                최근 워라밸과 직원 웰빙이 중요해지면서, ○○기업이 유연근무제 도입과 직원 심리상담 프로그램을 확대한 것을 보며 인재를 진정으로 소중히 여긴다고 느꼈습니다."
                <br/><br/>
                <strong>[입사후포부]</strong> "단기적으로는 채용과 온보딩 프로세스 개선을 통해 신입사원 적응률 90% 이상을 달성하겠습니다. 
                장기적으로는 조직문화 진단과 HR 데이터 분석을 바탕으로 전략적 인사정책을 수립하는 HR 전문가로 성장하고 싶습니다. 
                이를 위해 인사 관련 자격증을 취득하고, 조직심리학 지식을 습득하며, 글로벌 HR 트렌드를 지속적으로 연구하겠습니다."
              </p>
            </div>
          </div>
        </div>

        {/* 피해야 할 실수들 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">⚠️</span>
            절대 하면 안 되는 실수들
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border-l-4 border-red-500`}>
              <h3 className="font-bold mb-3 text-red-600">❌ 지원동기 실수</h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>"안정적이어서"</strong> - 도전 의지 없어 보임</li>
                <li>• <strong>"연봉이 좋아서"</strong> - 돈만 중시하는 것 같음</li>
                <li>• <strong>"집에서 가까워서"</strong> - 업무 열정 의심</li>
                <li>• <strong>"유명한 회사라서"</strong> - 구체적 이유 없음</li>
                <li>• <strong>"배우고 싶어서"</strong> - 너무 수동적</li>
                <li>• <strong>일반적인 칭찬 나열</strong> - 차별화 포인트 없음</li>
              </ul>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-50'} border-l-4 border-orange-500`}>
              <h3 className="font-bold mb-3 text-orange-600">❌ 입사후포부 실수</h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>"열심히 하겠습니다"</strong> - 구체성 부족</li>
                <li>• <strong>"배우고 성장하겠습니다"</strong> - 수동적 표현</li>
                <li>• <strong>"임원이 되고 싶습니다"</strong> - 너무 성급함</li>
                <li>• <strong>비현실적 목표 설정</strong> - 신뢰도 하락</li>
                <li>• <strong>회사와 무관한 개인 목표</strong> - 적합성 의심</li>
                <li>• <strong>추상적 표현만 사용</strong> - 실행력 의문</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'} border-l-4 border-green-500`}>
              <h3 className="font-bold mb-3 text-green-600">✅ 지원동기 좋은 답변</h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>개인 가치관 명확히 제시</strong></li>
                <li>• <strong>최근 이슈/트렌드 반영</strong></li>
                <li>• <strong>회사 고유 특징 언급</strong></li>
                <li>• <strong>구체적 사례와 연결</strong></li>
                <li>• <strong>나의 역량과 회사 니즈 연결</strong></li>
                <li>• <strong>회사에 대한 깊은 이해</strong></li>
              </ul>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <h3 className="font-bold mb-3 text-blue-600">✅ 입사후포부 좋은 답변</h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>단기/장기 목표 구체적 제시</strong></li>
                <li>• <strong>현실적이고 측정 가능한 목표</strong></li>
                <li>• <strong>목표 달성을 위한 구체적 방법</strong></li>
                <li>• <strong>회사 성장과 연결된 개인 성장</strong></li>
                <li>• <strong>직무 전문성 발전 계획</strong></li>
                <li>• <strong>지속적 학습 의지 표현</strong></li>
              </ul>
            </div>
          </div>
        </div>

        {/* 연습 가이드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            효과적인 연습 방법
          </h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">1️⃣ 회사 조사 철저히 하기</h3>
              <p className="text-sm leading-relaxed mb-4">
                회사 홈페이지, 뉴스, IR 자료, 임직원 인터뷰 등을 통해 깊이 있게 조사하세요.
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-blue-600">체크리스트:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 기업 비전과 미션 확인</li>
                  <li>• 최근 3년간 주요 뉴스와 성과</li>
                  <li>• 경쟁사 대비 차별화 포인트</li>
                  <li>• 기업 문화와 핵심 가치</li>
                  <li>• 최근 업계 이슈와 트렌드</li>
                </ul>
              </div>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">2️⃣ 나만의 스토리 정리하기</h3>
              <p className="text-sm leading-relaxed mb-4">
                본인의 경험을 회사와 연결할 수 있는 스토리로 정리하세요.
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-green-600">정리할 내용:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 나의 핵심 가치관과 기준</li>
                  <li>• 관련 경험과 성과</li>
                  <li>• 회사에 기여할 수 있는 역량</li>
                  <li>• 단기/장기 커리어 목표</li>
                  <li>• 목표 달성을 위한 구체적 계획</li>
                </ul>
              </div>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">3️⃣ 실전 연습하기</h3>
              <p className="text-sm leading-relaxed mb-4">
                거울 앞에서 또는 녹음하면서 실제처럼 연습해보세요.
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-purple-600">연습 포인트:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 지원동기 1분, 입사후포부 1분 내에 각각 완료</li>
                  <li>• 자연스러운 목소리 톤과 속도</li>
                  <li>• 핵심 키워드 강조하기</li>
                  <li>• 진정성 있는 표현과 자신감</li>
                  <li>• 질문 연계성 고려한 답변 준비</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 뒤로가기 */}
        <div className="text-center">
          <Link 
            to="/guide-success" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            ← 면접성공가이드로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default MotivationGuide;